import { useCallback, useEffect, useState } from "react";
import type { Location } from "@/types/location";
import {
  fetchRoleplayOpening,
  fetchRoleplayScenarioList,
  fetchRoleplayScore,
  fetchRoleplayTurn,
  fetchTrainingProgress,
  saveTrainingSession,
  type RoleplayLine,
  type RoleplayScenarioListItem,
  type RoleplayScore,
} from "@/lib/training/trainingApiClient";
import { useSpeechDictation } from "@/hooks/useSpeechDictation";
import { useAuthOptional } from "@/contexts/AuthContext";
import { canEditTrainingRoleplayScenarios, canHumanReviewRoleplay } from "@/lib/training/trainingReviewRoles";
import { speakRoleplayCustomerLine } from "@/components/lead/sections/trainingRoleplaySpeak";

export type TrainingRoleplayProgress = {
  totalSessions: number;
  bestOverallScore: number;
  lastSessionAt: string | null;
  byScenario: Record<string, number>;
  pendingHumanReviews?: number;
};

export function useTrainingRoleplayPanel(organizationId: string | null, location: Location | null) {
  const locationId = location?.id?.trim() ? location.id.trim() : null;
  const locationKey = locationId;

  const auth = useAuthOptional();
  const canReview = auth?.status === "ready" && canHumanReviewRoleplay(auth.hasRole);
  const includePlatformExample = auth?.status === "ready" && canEditTrainingRoleplayScenarios(auth.hasRole);

  const dictation = useSpeechDictation();

  const [scenarioKey, setScenarioKey] = useState<string>("");
  const [scenarioOptions, setScenarioOptions] = useState<RoleplayScenarioListItem[]>([]);
  const [messages, setMessages] = useState<RoleplayLine[]>([]);
  const [input, setInput] = useState("");
  const [phase, setPhase] = useState<"pick" | "live" | "scored">("pick");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [score, setScore] = useState<RoleplayScore | null>(null);
  const [progressErr, setProgressErr] = useState<string | null>(null);
  const [progress, setProgress] = useState<TrainingRoleplayProgress | null>(null);

  const loadProgress = useCallback(async () => {
    if (!organizationId) return;
    setProgressErr(null);
    const r = await fetchTrainingProgress(organizationId);
    if (!r.ok) {
      setProgressErr(r.error);
      return;
    }
    const d = r.data as {
      totalSessions?: number;
      bestOverallScore?: number;
      lastSessionAt?: string | null;
      byScenario?: Record<string, number>;
      pendingHumanReviews?: number;
    };
    if (d && typeof d === "object" && "totalSessions" in d) {
      setProgress({
        totalSessions: d.totalSessions ?? 0,
        bestOverallScore: d.bestOverallScore ?? 0,
        lastSessionAt: d.lastSessionAt ?? null,
        byScenario: d.byScenario ?? {},
        pendingHumanReviews: typeof d.pendingHumanReviews === "number" ? d.pendingHumanReviews : undefined,
      });
    }
  }, [organizationId]);

  useEffect(() => {
    void loadProgress();
  }, [loadProgress]);

  useEffect(() => {
    if (!organizationId) {
      setScenarioOptions([]);
      return;
    }
    void (async () => {
      const r = await fetchRoleplayScenarioList(organizationId, {
        includePlatformExample: includePlatformExample,
      });
      if (!r.ok) return;
      const d = r.data as { scenarios?: RoleplayScenarioListItem[] };
      const list = Array.isArray(d.scenarios) ? d.scenarios : [];
      setScenarioOptions(list);
      setScenarioKey((prev) => {
        if (!list.length) return "";
        if (list.some((x) => x.key === prev)) return prev;
        return list[0]!.key;
      });
    })();
  }, [organizationId, includePlatformExample]);

  const reset = () => {
    if (dictation.isListening) dictation.stop();
    dictation.reset();
    setMessages([]);
    setInput("");
    setPhase("pick");
    setScore(null);
    setError(null);
  };

  const startSession = async () => {
    if (!organizationId) return;
    if (!scenarioKey.trim()) {
      setError("No scenario is available. Ask a trainer to add scenarios under Manager → Training Authoring.");
      return;
    }
    setError(null);
    setLoading(true);
    setScore(null);
    try {
      const r = await fetchRoleplayOpening({
        organizationId,
        locationId,
        scenarioKey,
      });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      const data = r.data as { customerLine?: string; ok?: boolean; error?: string };
      if (!data || typeof data.customerLine !== "string") {
        setError(typeof data?.error === "string" ? data.error : "Unexpected response.");
        return;
      }
      setMessages([{ role: "customer", content: data.customerLine }]);
      setPhase("live");
      speakRoleplayCustomerLine(data.customerLine);
    } finally {
      setLoading(false);
    }
  };

  const sendSalesLine = async (lineOverride?: string) => {
    const line = (lineOverride ?? input).trim();
    if (!line || !organizationId || phase !== "live") return;
    setError(null);
    setLoading(true);
    try {
      const prior = [...messages, { role: "salesperson" as const, content: line }];
      setInput("");
      const r = await fetchRoleplayTurn({
        organizationId,
        locationId,
        scenarioKey,
        messages,
        salespersonLine: line,
      });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      const data = r.data as { customerLine?: string; error?: string };
      if (!data || typeof data.customerLine !== "string") {
        setError(typeof data?.error === "string" ? data.error : "Unexpected response.");
        return;
      }
      setMessages([...prior, { role: "customer", content: data.customerLine }]);
      speakRoleplayCustomerLine(data.customerLine);
    } finally {
      setLoading(false);
    }
  };

  const confirmDictationReply = () => {
    const text = dictation.draft.trim();
    if (!text || phase !== "live") return;

    if (dictation.isListening) dictation.stop();

    setInput(text);
    window.setTimeout(() => {
      void sendSalesLine(text);
      dictation.reset();
    }, 0);
  };

  const endAndScore = async () => {
    if (!organizationId || messages.length < 2) {
      setError("Have at least one exchange before ending.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const r = await fetchRoleplayScore({
        organizationId,
        locationId,
        scenarioKey,
        messages,
      });
      if (!r.ok) {
        setError(r.error);
        return;
      }
      const data = r.data as { score?: RoleplayScore; error?: string };
      if (!data?.score) {
        setError(typeof data?.error === "string" ? data.error : "Scoring failed.");
        return;
      }
      setScore(data.score);
      setPhase("scored");

      const save = await saveTrainingSession({
        organizationId,
        scenarioKey,
        locationKey,
        messages,
        score: data.score,
        summary: data.score.summary,
      });
      if (!save.ok) {
        setProgressErr(`Saved score locally but log failed: ${save.error}`);
      } else {
        await loadProgress();
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    organizationId,
    scenarioKey,
    setScenarioKey,
    scenarioOptions,
    messages,
    input,
    setInput,
    phase,
    loading,
    error,
    setError,
    score,
    progressErr,
    progress,
    canReview,
    dictation,
    reset,
    startSession,
    sendSalesLine,
    confirmDictationReply,
    endAndScore,
  };
}

export type TrainingRoleplayPanelVm = ReturnType<typeof useTrainingRoleplayPanel>;
